export const hasValidResponseActionId = (actionId) =>
    actionId !== null && actionId !== undefined && actionId !== 0;

export const hasValidResponseOutfitId = (outfitId) =>
    outfitId !== null && outfitId !== undefined;

export const hasValidResponseLocation = (location) => {
    if (location === null || location === undefined) return false;
    return location !== 0 && location !== '0';
};

const parseNumericId = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numericValue = Number.parseInt(String(value), 10);
    return Number.isNaN(numericValue) ? null : numericValue;
};

const normalizeOutfitId = (value, fallbackOutfitId = 0) => {
    if (value === undefined || value === null || value === '') {
        return fallbackOutfitId ?? 0;
    }
    return value;
};

const resolveActionIdFromSources = (...sources) => {
    for (const source of sources) {
        const parsedValue = parseNumericId(source);
        if (parsedValue !== null) return parsedValue;
    }
    return null;
};

const resolveCharacterStateDiffEntry = (stateDiff, characterId) => {
    if (!stateDiff || typeof stateDiff !== 'object') return null;
    const normalizedCharacterId = String(characterId ?? '');
    if (!normalizedCharacterId) return null;

    if (Array.isArray(stateDiff.activeCharacters)) {
        const activeMatch = stateDiff.activeCharacters.find(
            (character) => String(character?.id ?? character?.characterId ?? character?.character_id) === normalizedCharacterId
        );
        if (activeMatch) return activeMatch;
    }

    if (Array.isArray(stateDiff.characters)) {
        const characterMatch = stateDiff.characters.find(
            (character) => String(character?.id ?? character?.characterId ?? character?.character_id) === normalizedCharacterId
        );
        if (characterMatch) return characterMatch;
    }

    return null;
};

export const resolveMessageSpriteData = (
    message = {},
    options = {}
) => {
    const {
        characterId = message?.characterId,
        fallbackOutfitId = 0
    } = options;

    let actionId = resolveActionIdFromSources(
        message?.actionId,
        message?.action_id,
        message?.sprite_state
    );
    let outfitId = normalizeOutfitId(message?.outfitId ?? message?.outfit_id, fallbackOutfitId);
    let isVideo = message?.isVideo === true || message?.is_video === true;

    const messageStateDiff = message?.stateDiff ?? message?.state_diff;
    const stateDiffCharacter = resolveCharacterStateDiffEntry(messageStateDiff, characterId);
    if (stateDiffCharacter) {
        if (actionId === null) {
            actionId = resolveActionIdFromSources(
                stateDiffCharacter.actionId,
                stateDiffCharacter.action_id,
                stateDiffCharacter.sprite_state
            );
        }
        outfitId = normalizeOutfitId(
            stateDiffCharacter.outfitId ?? stateDiffCharacter.outfit_id,
            normalizeOutfitId(outfitId, fallbackOutfitId)
        );
        if (stateDiffCharacter.isVideo !== undefined || stateDiffCharacter.is_video !== undefined) {
            isVideo = stateDiffCharacter.isVideo === true || stateDiffCharacter.is_video === true;
        }
    }

    return {
        actionId,
        actionName: message?.actionName || stateDiffCharacter?.actionName || null,
        outfitId,
        isVideo
    };
};

export const resolveLatestCharacterSpriteData = (options = {}) => {
    const {
        messages = [],
        characterId,
        metadata = null,
        config = null
    } = options;

    const normalizedCharacterId = String(characterId ?? '');
    if (!normalizedCharacterId) return { actionId: null, spriteState: null };

    if (Array.isArray(messages) && messages.length > 0) {
        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            const messageCharacterId = message?.characterId ?? message?.character_id;
            if (String(messageCharacterId ?? '') !== normalizedCharacterId) continue;
            const isRosterNotice = message?.isJoinMessage === true || message?.isLeaveMessage === true;
            if (message?.role !== 'assistant' && !isRosterNotice) continue;

            const spriteData = resolveMessageSpriteData(message, {
                characterId: normalizedCharacterId,
                fallbackOutfitId: 0
            });
            if (spriteData.actionId !== null) {
                return {
                    actionId: spriteData.actionId,
                    spriteState: String(spriteData.actionId),
                    outfitId: spriteData.outfitId,
                    isVideo: spriteData.isVideo
                };
            }
        }
    }

    const metadataAction = parseNumericId(metadata?.lastSpriteState ?? metadata?.lastEmotion);
    if (metadataAction !== null) {
        return {
            actionId: metadataAction,
            spriteState: String(metadataAction)
        };
    }

    const allCharacters = Array.isArray(config?.allCharacters) ? config.allCharacters : [];
    const configCharacter = allCharacters.find(
        (character) => String(character?.id) === normalizedCharacterId
    );
    const configAction = resolveActionIdFromSources(
        configCharacter?.actionId,
        configCharacter?.spriteState?.action
    );

    if (configAction !== null) {
        return {
            actionId: configAction,
            spriteState: String(configAction)
        };
    }

    return { actionId: null, spriteState: null };
};
